import React, { FC, memo, useCallback, useEffect } from 'react';
import { Form } from 'antd';

import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Input } from 'shared/ui/Input';
import { Select } from 'shared/ui/Select';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Zone, ZoneObject, ZoneObjectType, useUpdateZoneMutation, UpdateZoneFormParams } from 'entities/Zone';
import { DrawerMode } from '../model/types';
import { UsersSelect } from './UsersSelect';
import { ObjectsSelect } from './ObjectsSelect';

const encode = (o: ZoneObject): string => `${o.objectType}:${o.objectId}`;
const decode = (v: string): ZoneObject => {
  const [type, id] = v.split(':');
  return { objectType: type as ZoneObjectType, objectId: id };
};

interface ZoneFormProps {
  zone?: Nullable<Zone>;
  mode: Nullable<DrawerMode>;
  handleClose: () => void;
}

export const ZoneForm: FC<ZoneFormProps> = memo((props) => {
  const { zone, mode, handleClose } = props;
  const { t } = useAppTranslation(['common', 'warehouses']);

  const [form] = Form.useForm();

  const [updateZone, { isLoading }] = useUpdateZoneMutation();
  const { data: warehouses } = useGetAllWarehousesQuery();

  useEffect(() => {
    if (zone) {
      form.setFieldsValue({
        name: zone.name,
        warehouseId: zone.warehouse?.warehouseId,
        users: zone.users?.map((u) => u.userId) ?? [],
        objects: zone.objects?.map(encode) ?? [],
      });
    } else {
      form.resetFields();
    }
  }, [zone, form]);

  const warehouseOptions = warehouses?.map((w) => ({ label: w.name, value: w.warehouseId })) ?? [];

  const handleSubmit = useCallback(
    async (data: UpdateZoneFormParams) => {
      try {
        const payload = {
          name: data.name || '',
          warehouseId: data.warehouseId || '',
          users: (data.users || []).map((userId) => ({ userId })),
          objects: (data.objects || []).map(decode),
        };

        if (zone) {
          await updateZone({ zoneId: zone.zoneId, ...payload }).unwrap();

          showNotification('info', t('Success', { ns: 'common' }), t('Zone has been successfully updated', { ns: 'common' }));
        }

        handleClose();
      } catch (error) {
        console.error(error);
        showNotification('error', t('Error', { ns: 'common' }), t('Something went wrong', { ns: 'common' }));
      }
    },
    [updateZone, zone, mode, handleClose, t],
  );

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col px-10 py-4">
      <div className="flex justify-between items-center pt-4 pb-10">
        <div className="font-semibold text-3xl">
          {t('Edit')} {zone?.name || ''}
        </div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={handleClose}>
          {t('Close', { ns: 'common' })}
        </div>
      </div>

      <Form.Item
        label={t('Zone name')}
        name="name"
        rules={[{ required: true, message: t('Please, enter zone name', { ns: 'warehouses' }) }]}
      >
        <Input placeholder={t('Please, enter zone name', { ns: 'warehouses' })} bordered />
      </Form.Item>

      <Form.Item label={t('Warehouse')} name="warehouseId" rules={[{ required: true, message: t('Please, select warehouse') }]}>
        <Select options={warehouseOptions} placeholder={t('Please, select warehouse')} bordered disabled />
      </Form.Item>

      <Form.Item label={t('Users')} required>
        <Form.Item name="users" noStyle>
          <UsersSelect
            value={form.getFieldValue('users')}
            onChange={(val) => {
              form.setFieldsValue({ users: val });
            }}
            onClear={() => {
              form.setFieldsValue({ users: [] });
            }}
          />
        </Form.Item>
      </Form.Item>

      <Form.Item label={t('Units')} name="objects">
        <ObjectsSelect
          warehouseId={zone?.warehouse?.warehouseId}
          value={form.getFieldValue('objects')}
          onChange={(val) => {
            form.setFieldsValue({ objects: val });
          }}
          onClear={() => {
            form.setFieldsValue({ objects: [] });
          }}
        />
      </Form.Item>

      <Button type="submit" isLoading={isLoading}>
        {t('Save', { ns: 'common' })}
      </Button>
    </Form>
  );
});
